/** @jsxImportSource ./simboluri-jsx */
import { textVerdict } from "./elemente";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { ArmareResult, CroireAx, FemModel, GrindaArmata, StepState, fmtDate } from "./tipuri";
import TitluVerificare, { useCatalogVerificari } from "./Verificare";
import Info from "./Info";
import GammaRdTabel, { zecGamma } from "./GammaRdTabel";
import LocalizareCladire from "./LocalizareCladire";
import { cutiaPiesei, incadreaza } from "./incadrare3d";
import { peDeschidere } from "./PuncteGrinda";
import { Focus } from "./legaturi";
import { DescriereaPasului } from "./TitluPas";
import { CotaH, CotaV, HasuraPlaca, cmFmt, mFmt } from "./cote";

import NotePas from "./NotePas";
import { t, txn, tx, useT } from "../i18n";
import type { Cheie } from "../i18n";
import { numelePunctului } from "./puncte";
// Pasul 13 — Armarea grinzilor: pagina unei grinzi = «nota de calcul»
// completă — 3D cu armătura, eforturile din care s-a plecat, fiecare formulă
// cu valorile grinzii, toate verificările cu R/E și verdict,
// încercărilor solverului și interfața către pasul stâlpilor.

const PUNCTE = ["R1_sus", "R1_jos", "camp", "R2_sus", "R2_jos"] as const;

function fmt(v: number | null | undefined, dec = 1): string {
  if (v === null || v === undefined) return "—";
  return v.toFixed(dec);
}

// Formatări ro-RO pentru zonele rigide: metrii cu 2 zecimale și virgulă
// zecimală, centimetrii rotunzi (un „rigid 16,2 cm" n-ar spune nimic în plus).
function fmtM(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v.toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
function fmtCm(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return Math.round(v * 100).toLocaleString("ro-RO");
}

// Zonele de la tăietoare: cheia vine de la server, eticheta din catalog.
const ZONE_TAIETOARE: Record<"fundamentala" | "zona_critica" | "zona_curenta", Cheie> = {
  fundamentala: "struct.arg.zona_fundamentala",
  zona_critica: "struct.arg.zona_critica",
  zona_curenta: "struct.arg.zona_curenta",
};

function Verdict({ v, marja }: { v: string; marja?: number | null }) {
  const cls = v.startsWith("SE VERIFICA")
    ? "arm-ok"
    : v.startsWith("ATENTIE")
      ? "arm-warn"
      : v.startsWith("NU")
        ? "arm-bad"
        : "arm-neutral";
  return (
    <>
      <span className={`arm-verdict ${cls}`}>{textVerdict(v)}</span>
      {marja ? (
        <span
          className="arm-verdict arm-warn"
          style={{ marginLeft: 4 }}
          title={t("struct.arg.r_sub_1", { p1: marja })}
        >
          {t("struct.arp.prin_marja", { marja })}
        </span>
      ) : null}
    </>
  );
}

export default function StepArmareGrinzi({
  state,
  femState,
  running,
  focus,
  projectId,
  solutie,
}: {
  state: StepState | null;
  // modelul Pasului 7, doar pentru vizualizarea «unde e grinda în clădire»
  femState?: StepState | null;
  running: boolean;
  // grinda cerută dintr-un link (?focus=GR18.1) — v. legaturi.ts
  focus?: Focus;
  // proiectul + soluția vizualizată: viewerul «unde e în clădire» le folosește
  // ca să poată sări la stâlpul sau la peretele pe care dai click
  projectId?: string;
  solutie?: string | null;
}) {
  useT();
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.arg.pasul_ruleaza")}</strong>
          <div className="sg-muted">
            {t("struct.arg.grinda_extrag_eforturile")}
          </div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.arg.pasul_nu_rulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="armare_grinzi" />
      </div>
    );
  }

  const r = state.result as unknown as ArmareResult;
  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.arg.ultima_rulare_esuat", {
            motiv: r?.error ?? t("struct.arg.eroare_necunoscuta"),
          })}
        </div>
        <div className="sg-muted">{t("struct.arg.ruleaza_nou_meniul")}</div>
      </div>
    );
  }
  return <ArmareView r={r} model={modelFem(femState)} updatedAt={state.updated_at}
                     focus={focus} projectId={projectId} solutie={solutie} />;
}

// ---------------------------------------------------------------------------

// modelul FEM din starea Pasului 7, dacă a rulat
function modelFem(st: StepState | null | undefined): FemModel | null {
  return ((st?.result as { model?: FemModel } | null)?.model) ?? null;
}

function ArmareView({ r, model, updatedAt, focus, projectId, solutie }: {
  r: ArmareResult;
  model: FemModel | null;
  updatedAt: string;
  focus?: Focus;
  projectId?: string;
  solutie?: string | null;
}) {
  useT();
  const [sel, setSel] = useState<string>("");
  // linkul dintr-o verificare picată (Pasul 19) aduce chiar id-ul de plan al
  // grinzii, cu sufixul de nivel — exact cheia selectorului
  useEffect(() => {
    if (focus?.ref) setSel(focus.ref);
  }, [focus?.n, focus?.ref]);
  const grinzi = useMemo(
    () =>
      [...r.grinzi].sort((a, b) =>
        a.element.localeCompare(b.element, undefined, { numeric: true })
      ),
    [r]
  );
  const g = grinzi.find((x) => `${x.element}` === sel) ?? grinzi[0];
  const p = r.parametri;

  return (
    <div className="st-result">
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.arg.grinzi_armate")}
            <Info
              titlu={t("struct.arg.ce_face_pasul")}
              ce={t("struct.arg.alege_armarea_fiecarei")}
              deCe={t("struct.arg.modelul_fem_contine")}
              parametri={[
                { simbol: "R/E", nume: t("struct.arg.raportul_capabil_efectiv"), sursa: t("struct.arg.foaia_b") },
                { simbol: "γ_Rd", nume: t("struct.arg.suprarezistenta_calcul_c", { p1: zecGamma(p.gamma_rd), p2: p.gamma_rd_sursa }), sursa: t("struct.ars.clasa_ductilitate_pas3") },
                { simbol: t("struct.arg.simbol_marja"), nume: t("struct.arg.marja_subdimensionare_ac", { p1: p.acceptare_grinzi_pct ?? 0, p2: p.prag_re_acceptare ?? 1 }), sursa: t("struct.arg.constanta_a_metodei") },
              ]}
            />
          </div>
          <div className="st-card-value">{r.nr_grinzi}</div>
          <div className="st-card-sub">
            {Object.entries(r.verdicte)
              .map(([k, v]) => `${v} × ${k}`)
              .join(" · ")}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">{t("struct.arg.parametrii_de_calcul")}</div>
          <div className="st-card-sub" style={{ marginTop: 6 }}>
            {t("struct.arg.param_beton_otel", {
              beton: p.beton, fcd: p.f_cd_mpa, fctd: p.f_ctd_mpa,
              otel: p.otel_longitudinal, fyd: p.f_yd_mpa,
              clasa: p.clasa_ductilitate, beff: p.b_eff,
              csus: p.c_sus_cm, cjos: p.c_jos_cm, placa: p.armare_placa,
            })}
          </div>
          <div className="st-card-sub" style={{ marginTop: 6 }}>
            {t("struct.arg.param_gamma", {
              g1: zecGamma(p.gamma_rd), g2: zecGamma(p.gamma_rd_ancorare),
              sursa: p.gamma_rd_sursa,
            })}
          </div>
        </div>
      </div>

      <GammaRdTabel
        randuri={r.gamma_rd_tabel}
        clasa={String(p.clasa_ductilitate ?? "")}
      />

      <NotePas
        note={r.avertismente}
        titlu="struct.note.armarii"
        ce="struct.note.armarii_ce"
      />

      <div className="st-findings">
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label className="sg-muted">
            {t("struct.arg.lbl_grinda")}{" "}
            <select value={g?.element ?? ""} onChange={(e) => setSel(e.target.value)}>
              {grinzi.map((x) => (
                <option key={x.element} value={x.element}>
                  {t("struct.arg.optiune_grinda", {
                    el: x.element, nivel: x.nivel, verdict: x.verdict,
                  })}
                </option>
              ))}
            </select>
          </label>
          {g && <Verdict v={g.verdict} />}
          {g && (
            <span className="sg-muted">
              {t("struct.arg.grinda_dimensiuni", {
                b: g.b_cm, h: g.h_cm, l: g.L_ax_m, l0: g.l0_m,
                si: g.stalp_i ?? "?", sj: g.stalp_j ?? "?",
              })}
              {/* zonele rigide: bucata de grindă din interiorul stâlpului. Se
                  arată în titlu pentru că ele explică diferența L_ax − l₀ —
                  altfel cele două lungimi par o inconsecvență */}
              {(g.off_i_m || g.off_j_m) ? (
                <span title={t("struct.arg.portiunile_grinda_interi")}>
                  {t("struct.arg.rigid_off", {
                    i: fmtCm(g.off_i_m), j: fmtCm(g.off_j_m),
                  })}
                </span>
              ) : null}
            </span>
          )}
        </div>
        <div className="sg-muted" style={{ marginTop: 6 }}>
          {tx("struct.arg.verdictul_aduna")}
          {Number(p.acceptare_grinzi_pct ?? 0) > 0 && (
            <>
              {" "}
              {tx("struct.arg.marja_acceptata", {
                prag: String(p.prag_re_acceptare),
                pct: String(p.acceptare_grinzi_pct),
              })}
            </>
          )}
        </div>
      </div>

      {g && (
        <NotaCalcul
          g={g}
          p={p}
          croire={croireAxului(r, g)}
          model={model}
          projectId={projectId}
          solutie={solutie}
          // click pe ALTĂ grindă din viewer = se schimbă selecția aici, fără
          // să se reîncarce pagina (și fără să sară camera)
          peGrinda={(ref) => setSel(ref)}
        />
      )}

      <div className="sg-muted st-ran-at">{t("struct.pas.rulat_la", { cand: fmtDate(updatedAt) })}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nota de calcul a unei grinzi
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// CROIALA barelor: bucățile și suprapunerile
// ---------------------------------------------------------------------------
// Oțelul se livrează la 12 m, deci o bară care merge pe tot axul se taie. Unde
// se taie nu e o chestiune de metraj, ci de rezistență: pe lungimea
// suprapunerii efortul trece dintr-o bucată în cealaltă prin beton, deci
// înnădirea stă pe fibra COMPRIMATĂ — barele de sus în câmp, cele de jos lângă
// reazem. Tabelul arată exact asta, pe abscisa axului.

function CroialaBare({ croire }: { croire?: CroireAx | null }) {
  useT();
  if (!croire || !croire.trasee.length) return null;
  const cuInnadiri = croire.trasee.filter((tr) => tr.n_innadiri > 0);
  return (
    <div className="st-findings">
      <h4>
        {t("struct.arg.croiala_barelor")}
        <Info
          titlu={t("struct.arg.taie_bara")}
          ce={t("struct.arg.otelul_livreaza_bare")}
          deCe={t("struct.arg.lungimea_suprapunerii_ba")}
        />
      </h4>
      <table className="sg-table">
        <thead>
          <tr>
            <th>{t("struct.arg.th_bara")}</th>
            <th>n × Ø</th>
            <th>{t("struct.arg.th_traseul")}</th>
            <th>{t("struct.arg.bucati")}</th>
            <th>{t("struct.arg.th_suprapuneri")}</th>
            <th>{t("struct.arg.otel_bara_m")}</th>
          </tr>
        </thead>
        <tbody>
          {croire.trasee.map((tr, k) => (
            <tr key={k}>
              <td>{tr.rol}</td>
              <td>
                {tr.n}Ø{tr.phi_mm}
              </td>
              <td>
                {mFmt(tr.s1_m)} → {mFmt(tr.s2_m)} ({mFmt(tr.l_traseu_m)})
              </td>
              <td>{tr.bucati.map((b) => mFmt(b.L_m)).join(" + ")}</td>
              <td>
                {tr.n_innadiri === 0
                  ? "—"
                  : tr.innadiri
                      .map(
                        (i) =>
                          t("struct.arg.innadire_la", {
                            s: mFmt(i.s_m), l0: mFmt(i.l0_m),
                          }) + (i.zona
                            ? t("struct.arg.innadire_zona", { zona: i.zona })
                            : t("struct.arg.innadire_fara_zona"))
                      )
                      .join(" · ")}
              </td>
              <td>{mFmt(tr.l_croita_m)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sg-muted" style={{ fontSize: 12, marginTop: 6 }}>
        {t("struct.arg.abscisa_din_capat", {
          ax: croire.ax ? ` ${croire.ax}` : "",
          l: mFmt(croire.L_ax_m),
          noduri: croire.noduri_m.map((n) => mFmt(n)).join(", "),
        })}
        {cuInnadiri.some((tr) => tr.n_fortate > 0) && (
          <>
            {" "}
            <strong style={{ color: "#b5323d" }}>
              {t("struct.arg.suprapuneri_fortate")}
            </strong>
            {t("struct.arg.suprapuneri_fortate_de_ce")}
          </>
        )}
      </div>
    </div>
  );
}

/** Croiala axului din care face parte grinda (Pasul 13). O grindă singură pe
 *  direcția ei are propriul rând, cu `ax: null`. */
export function croireAxului(r: ArmareResult | null | undefined,
                             g: GrindaArmata | null | undefined) {
  if (!r?.croire || !g) return null;
  return r.croire.find(
    (c) => c.nivel === g.nivel && c.grinzi.includes(g.element)) ?? null;
}

/** Nota de calcul a unei grinzi. `faraVederi` lasă deoparte scena 3D și
 *  localizarea din capul ei: pagina elementului le are deja, deasupra. */
export function NotaCalcul({ g, p, croire, model, projectId, solutie, peGrinda,
                             faraVederi }: {
  g: GrindaArmata;
  p: ArmareResult["parametri"];
  croire?: CroireAx | null;
  model: FemModel | null;
  projectId?: string;
  solutie?: string | null;
  peGrinda?: (ref: string) => void;
  faraVederi?: boolean;
}) {
  useT();
  const geo = g.geometrie_sectiune;
  const arm = g.armare;
  // titlurile verificărilor: același catalog ca la Pașii 14, 15 și 16
  const catalog = useCatalogVerificari();
  const grupaS = g.seismic_necesar ? "seismica" : "fundamentala";
  const camp = g.puncte.fundamentala.camp;

  return (
    <>
      {/* --- 3D full-width -------------------------------------------------- */}
      <div className="st-findings" style={{ padding: 0, overflow: "hidden" }}>
        {/* detaliul grinzii + «unde e ea în clădire», una lângă alta: desenul
            de detaliu nu spune niciodată la ce etaj și în ce colț ești */}
        {!faraVederi && (
        <div style={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 480px", minWidth: 0 }}>
            <Grinda3D g={g} croire={croire} />
          </div>
          <div style={{ flex: "0 0 340px", maxWidth: "100%",
                        borderLeft: "1px solid #dedede" }}>
            <LocalizareCladire
              model={model}
              evidentiate={[g.element]}
              height={380}
              projectId={projectId}
              solutie={solutie}
              pasCurent="armare_grinzi"
              peElement={peGrinda}
            />
          </div>
        </div>
        )}
        <div className="sg-muted" style={{ padding: "6px 12px", fontSize: 12 }}>
          {t("struct.arg.legenda_scena", { lcr: g.interfata_stalpi.l_cr_m })}{" "}
          {tx("struct.arg.legenda_petrecere_a")}{" "}
          <strong style={{ color: "#b5323d" }}>
            {t("struct.arg.banda_rosie_scurt")}
          </strong>
          {tx("struct.arg.legenda_petrecere_b")}
        </div>
        {/* Nota despre zona rigidă: fără ea, cifra l₀ din titlu pare o
            greșeală de rotunjire față de L_ax. Explicația e și legătura cu
            Pasul 15 (ancorarea prin nod) — Stefan */}
        <div
          className="sg-muted"
          style={{ padding: "0 12px 10px", fontSize: 12, lineHeight: 1.55 }}
        >
          {tx("struct.arg.zona_rigida_explic", {
            i: fmtCm(g.off_i_m), j: fmtCm(g.off_j_m),
          })}
          <br />
          {tx("struct.arg.de_ce_conteaza_l0", {
            lax: fmtM(g.L_ax_m), oi: fmtM(g.off_i_m),
            oj: fmtM(g.off_j_m), l0: fmtM(g.l0_m),
          })}
          <br />
          <strong>{t("struct.arg.consecinta_practica")}</strong> {t("struct.arg.zona_rigida_lunga")}
        </div>
      </div>

      <CroialaBare croire={croire} />

      {/* --- 1. Geometria și secțiunea activă -------------------------------- */}
      <Sectiune
        titlu={t("struct.arg.1_geometria_sectiunea")}
        explicatie={t("struct.arg.grinda_p1_are", { p1: g.element, p2: g.b_cm, p3: g.h_cm, p4: g.h_pl_cm, p5: parteaPlacii(g.n_placi.R1), p6: parteaPlacii(g.n_placi.R2), p7: p.b_eff })}
        formule={[
          {
            eticheta: t("struct.arg.acoperirea_utila_sus"),
            valori: `a_sus = ${p.c_sus_cm} + Ø${maxPhiSus(g)}/20 ${rand2Sus(g) ? "+ 1,5" : ""} = ${geo.a_sus} cm`,
            ref: t("struct.arg.foaia_b_ref", { ref: "AV11" }),
          },
          {
            eticheta: t("struct.arg.inaltimea_utila_d"),
            valori: `d = ${g.h_cm} − ${geo.a_sus} = ${geo.d} cm`,
            ref: t("struct.arg.foaia_b_ref", { ref: "AV14" }),
          },
          {
            eticheta: t("struct.arg.distanta_intre_armaturi"),
            valori: `ds = ${geo.d} − ${geo.a_jos} = ${geo.ds} cm`,
            ref: t("struct.arg.foaia_b_ref", { ref: "AV15" }),
          },
          {
            eticheta: t("struct.arg.bratul_parghie_z"),
            valori: `z_R1 = ${geo.z.R1} cm; z_R2 = ${geo.z.R2} cm`,
            ref: t("struct.arg.foaia_b_ref", { ref: "AV16/AV17" }),
          },
        ]}
        termeni={[
          [t("struct.arg.termen_brat"), t("struct.arg.termen_brat_def")],
          [t("struct.arg.termen_d"), t("struct.arg.termen_d_def")],
          [t("struct.arg.termen_beff"), t("struct.arg.termen_beff_def")],
        ]}
      />

      {/* --- 2. Eforturile de calcul ----------------------------------------- */}
      <Sectiune
        titlu={t("struct.arg.s2_titlu")}
        explicatie={t("struct.arg.s2_explicatie", {
          corectie: camp.M_necorectat !== undefined
            ? t("struct.arg.deschiderea_depaseste_4", {
                p1: fmt(camp.M_necorectat), p2: fmt(camp.M),
              })
            : t("struct.arg.deschiderea_sub_4_5"),
        })}
      >
        <TabelPuncte g={g} />
      </Sectiune>

      {/* --- 3. E necesară armarea seismică? --------------------------------- */}
      <Sectiune
        titlu={t("struct.arg.3_combinatia_dimensionea")}
        explicatie={
          g.seismic_necesar
            ? t("struct.arg.s3_seismic_da", { coef: p.coef_placa_seism ?? 0.7 })
            : t("struct.arg.s3_seismic_nu")
        }
      />

      {/* --- 4. Armarea aleasă ----------------------------------------------- */}
      <Sectiune
        titlu={t("struct.arg.4_armarea_aleasa")}
        explicatie={t("struct.arg.s4_explicatie")}
      >
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <SectiuneTransversala g={g} unde="reazem" />
          <SectiuneTransversala g={g} unde="camp" />
          <div style={{ minWidth: 260 }}>
            <table className="sg-table">
              <tbody>
                <tr>
                  <td>
                    {t("struct.arg.sus_continuu")}
                    {arm.sus_impus_de_ax && (
                      <span className="ob-chip" style={{ fontSize: 10, marginLeft: 6 }}
                            title={t("struct.arg.carcasa_sus_axul", { ax: g.ax?.id ?? "" })}>
                        {t("struct.arg.ax")}
                      </span>
                    )}
                  </td>
                  <td className="sg-mono">{arm.sus.continue.n}Ø{arm.sus.continue.phi}</td>
                </tr>
                {arm.sus.supl_r1.n > 0 && <tr>
                  <td>
                    {t("struct.arg.sus_supl_r1")}{" "}
                    {arm.sus.supl_r1.rand === 2 ? t("struct.arg.randul_2") : ""}
                  </td>
                  <td className="sg-mono">{arm.sus.supl_r1.n}Ø{arm.sus.supl_r1.phi}</td>
                </tr>}
                {arm.sus.supl_r2.n > 0 && <tr><td>{t("struct.arg.sus_suplimentar_r2")}</td><td className="sg-mono">{arm.sus.supl_r2.n}Ø{arm.sus.supl_r2.phi}</td></tr>}
                <tr><td>{t("struct.arg.jos_continuu")}</td><td className="sg-mono">{arm.jos.continue.n}Ø{arm.jos.continue.phi}</td></tr>
                {arm.jos.supl_camp.n > 0 && (
                  <tr>
                    <td title={t(arm.mod_intrerupere === "intrerupte"
                      ? "struct.arg.bara_retezata_drept"
                      : "struct.arg.bara_ridicata_45")}>
                      {t("struct.arg.jos_supl_camp")}{" "}
                      {t(arm.mod_intrerupere === "intrerupte"
                        ? "struct.arg.retezate" : "struct.arg.ridicate_45")}
                      {arm.jos.supl_camp.rand === 2 ? ` ${t("struct.arg.randul_2")}` : ""}
                    </td>
                    <td className="sg-mono">{arm.jos.supl_camp.n}Ø{arm.jos.supl_camp.phi}</td>
                  </tr>
                )}
                <tr>
                  <td title={t("struct.arg.pasul_pune_multipli")}>
                    {arm.etrieri.pas_critic === arm.etrieri.pas_curent
                      ? t("struct.arg.etrieri_pas_unic")
                      : t("struct.arg.etrieri_doua_zone")}
                  </td>
                  <td className="sg-mono">
                    {arm.etrieri.pas_critic === arm.etrieri.pas_curent
                      ? `Ø${arm.etrieri.phi}/${arm.etrieri.pas_critic} cm`
                      : `Ø${arm.etrieri.phi}/${arm.etrieri.pas_critic} / Ø${arm.etrieri.phi}/${arm.etrieri.pas_curent} cm`}
                  </td>
                </tr>
                {arm.inclinate && <tr><td>{t("struct.arg.bare_inclinate_45")}</td><td className="sg-mono">{arm.inclinate.n}Ø{arm.inclinate.phi}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="sg-muted" style={{ marginTop: 10, lineHeight: 1.5 }}>
          {t("struct.arg.armarea_se_cauta")}{" "}
          {g.nr_incercari != null
            ? txn("struct.arg.incercari#n", g.nr_incercari)
            : null}{" "}
          {t("struct.arg.jurnalul_nu_se_pastreaza")}
        </div>
      </Sectiune>

      {/* --- 5. Momentele capabile + R/E -------------------------------------- */}
      <Sectiune
        titlu={t("struct.arg.5_momentele_capabile")}
        explicatie={t("struct.arg.s5_explicatie", {
          cf: p.coef_placa_fund ?? 0.8, cs: p.coef_placa_seism ?? 0.7,
        })}
      >
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.arg.th_punct")}</th><th>{t("struct.arg.th_grupare")}</th>
              <th title={t("struct.arg.momentul_de_calcul")}>M_Ed (kNm)</th>
              <th title={t("struct.arg.forta_axiala_concomitent")}>N (kN)</th>
              <th title={t("struct.arg.momentul_consumat_axial")}>M_c (kNm)</th>
              <th>M_Rd (kNm)</th><th>R/E</th><th>{t("struct.arg.th_verdict")}</th>
            </tr>
          </thead>
          <tbody>
            {g.verificari
              .filter((v) => v.nume.startsWith("M_Rd"))
              .map((v, i) => (
                <tr key={i}>
                  <td className="sg-mono" title={numelePunctului(v.punct)}>{v.punct}</td>
                  <td className="sg-muted">
                    <TitluVerificare catalog={catalog} pas="armare_grinzi"
                                     nume={v.nume} grupa={v.grupa}
                                     text={v.grupa} aratPasul={false}
                                     aratNumele={false} />
                  </td>
                  <td className="sg-mono">{typeof v.E === "number" ? fmt(v.E) : v.E}</td>
                  <td className="sg-mono">{fmt(v.N)}{v.regim_axial && v.regim_axial !== "-" ? ` (${v.regim_axial})` : ""}</td>
                  <td className="sg-mono">{fmt(v.M_c)}</td>
                  <td className="sg-mono"><strong>{typeof v.R === "number" ? fmt(v.R) : v.R}</strong></td>
                  <td className="sg-mono">{v.RE === null ? "—" : fmt(v.RE, 2)}</td>
                  <td><Verdict v={v.verdict} marja={v.acceptat_marja} /></td>
                </tr>
              ))}
          </tbody>
        </table>
        <FormulaExemplu g={g} p={p} />
      </Sectiune>

      {/* --- 6. Ductilitatea secțiunii ---------------------------------------- */}
      <Sectiune
        titlu={t("struct.arg.6_ductilitatea_sectiunii")}
        explicatie={t("struct.arg.s6_explicatie", { xu: fmt(0.25 * geo.d) })}
      >
        <div className="sg-mono" style={{ fontSize: 13 }}>
          {Object.entries(g.x_neutra).map(([k, v]) => (
            <span key={k} style={{ marginRight: 16 }}>
              {k}: x = {v} cm
            </span>
          ))}
        </div>
        <div style={{ marginTop: 6 }}>
          x_max = {fmt(Math.max(...Object.values(g.x_neutra)))} cm ≤ x_u ={" "}
          {fmt(0.25 * geo.d)} cm{" "}
          {(() => {
            const vd = g.verificari.find((v) => v.nume.startsWith("Ductilitatea"));
            return (
              <Verdict
                v={vd?.verdict ?? (Math.max(...Object.values(g.x_neutra)) <= 0.25 * geo.d ? "SE VERIFICA!" : "NU SE VERIFICA!!!")}
                marja={vd?.acceptat_marja}
              />
            );
          })()}
        </div>
        {(g.verificari.find((v) => v.nume.startsWith("Ductilitatea"))?.verdict ?? "").startsWith("NU") && (
          <div className="st-warn" style={{ marginTop: 6 }}>
            {t("struct.arg.zona_comprimata_prea")}
          </div>
        )}
      </Sectiune>

      {/* --- 7. Dirijarea articulației ----------------------------------------- */}
      <Sectiune
        titlu={t("struct.arg.7_dirijarea_articulatiei")}
        explicatie={t("struct.arg.s7_explicatie")}
      >
        {g.articulatie.posibila_in_camp ? (
          <div>
            {t("struct.arg.articulatie_praguri", {
              m: fmt(g.articulatie.M_Rd_camp),
              praguri: g.articulatie.praguri?.map((x) => fmt(x)).join(" / "),
            })}{" "}
            <Verdict v={g.articulatie.verdict} />
          </div>
        ) : (
          <div className="sg-muted">
            {t("struct.arg.articulatie_nu_e_cazul")}{" "}
            <Verdict v="SE VERIFICA!" />
          </div>
        )}
      </Sectiune>

      {/* --- 8. Forța tăietoare ------------------------------------------------ */}
      <Sectiune
        titlu={t("struct.arg.8_forta_taietoare")}
        explicatie={t("struct.arg.s8_explicatie")}
        formule={[
          {
            eticheta: "V_Ed,cap = max(M_Rd[R1s]+M_Rd[R2j], M_Rd[R2s]+M_Rd[R1j])·γ_Rd/L_as + V_grav",
            valori: `V_Ed,cap = max(${fmt(Math.abs(g.m_rd[grupaS].R1_sus))}+${fmt(Math.abs(g.m_rd[grupaS].R2_jos))}, ${fmt(Math.abs(g.m_rd[grupaS].R2_sus))}+${fmt(Math.abs(g.m_rd[grupaS].R1_jos))})·${p.gamma_rd}/${fmt(g.taietoare.zona_critica.L_as_m)} + ${fmt(Math.abs(g.V_gs_kn))} = ${fmt(g.taietoare.zona_critica.V_Ed)} kN`,
            ref: t("struct.arg.foaia_b_ref", { ref: "BT26; P100-1 §5.3.3.2" }),
          },
        ]}
      >
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.arl.th_zona")}</th><th>V_Ed (kN)</th><th>{t("struct.arg.th_pas_etrieri")}</th>
              <th title={t("struct.arg.q_10_v")}>Q̄</th>
              <th>V_Rd (kN)</th><th>R/E</th><th>{t("struct.arg.th_verdict")}</th>
            </tr>
          </thead>
          <tbody>
            {(["fundamentala", "zona_critica", "zona_curenta"] as const).map((k) => {
              const z = g.taietoare[k];
              return (
                <tr key={k}>
                  <td title={z.descriere}>{t(ZONE_TAIETOARE[k])}</td>
                  <td className="sg-mono">{fmt(z.V_Ed)}</td>
                  <td className="sg-mono">{z.pas_cm ? `Ø${g.armare.etrieri.phi}/${z.pas_cm} cm` : "—"}</td>
                  <td className="sg-mono">{fmt(z.Q_bar, 2)}</td>
                  <td className="sg-mono"><strong>{fmt(z.V_Rd)}</strong></td>
                  <td className="sg-mono">{z.RE === null || z.RE === undefined ? "—" : fmt(z.RE, 2)}</td>
                  <td><Verdict v={z.verdict ?? "-"} marja={z.acceptat_marja} /></td>
                </tr>
              );
            })}
            <tr>
              <td title={g.taietoare.V_Rd_max.descriere}>{t("struct.arg.strivirea_diagonalelor")}</td>
              <td className="sg-mono">{fmt(g.taietoare.V_Rd_max.V_Ed)}</td>
              <td className="sg-mono">—</td>
              <td className="sg-mono">—</td>
              <td className="sg-mono"><strong>{fmt(g.taietoare.V_Rd_max.V_Rd)}</strong></td>
              <td className="sg-mono">—</td>
              <td><Verdict v={g.taietoare.V_Rd_max.verdict ?? "-"} marja={g.taietoare.V_Rd_max.acceptat_marja} /></td>
            </tr>
            <tr>
              <td title={g.taietoare.EC2_etrieri.descriere}>{t("struct.arg.v_rd_s")}</td>
              <td className="sg-mono">{fmt(g.taietoare.EC2_etrieri.V_Ed)}</td>
              <td className="sg-mono">—</td>
              <td className="sg-mono">—</td>
              <td className="sg-mono">{fmt(g.taietoare.EC2_etrieri.V_Rd)}</td>
              <td className="sg-mono">—</td>
              <td>—</td>
            </tr>
          </tbody>
        </table>
        <div style={{ marginTop: 8 }}>
          <strong>{t("struct.arg.bare_inclinate")}</strong>{" "}
          {g.taietoare.bare_inclinate.necesare ? (
            <span>
              {t("struct.arg.inclinate_necesare", {
                zeta: fmt(g.taietoare.bare_inclinate.zeta, 2),
                n: g.taietoare.bare_inclinate.n,
                phi: g.taietoare.bare_inclinate.phi,
                v: fmt(g.taietoare.bare_inclinate.V_de_preluat_kn),
              })}{" "}
              <Verdict v={g.taietoare.bare_inclinate.verdict ?? "-"} marja={g.taietoare.bare_inclinate.acceptat_marja} />
            </span>
          ) : (
            <span className="sg-muted">
              {t("struct.arg.inclinate_nu_e_cazul", {
                zeta: fmt(g.taietoare.bare_inclinate.zeta, 2),
              })}
            </span>
          )}
        </div>
      </Sectiune>

      {/* --- 9. SLS ---------------------------------------------------------------- */}
      {g.sls && (
        <Sectiune
          titlu={t("struct.arg.9_sls_sageata")}
          explicatie={t("struct.arg.s9_explicatie", {
            e: g.sls.sageata.E_eff_mpa, phi: g.sls.sageata.phi_fluaj,
          })}
          formule={[
            {
              eticheta: t("struct.arg.momentul_de_fisurare"),
              valori: t("struct.arg.valori_mcr", {
                mcr: fmt(g.sls.sageata.M_cr_knm), ii: g.sls.sageata.I_I_cm4,
                iii: g.sls.sageata.I_II_camp_cm4,
                x: g.sls.sageata.x_II_camp_cm, zeta: g.sls.sageata.zeta_camp,
              }),
              ref: "EC2 §7.4.3",
            },
            {
              eticheta: t("struct.arg.sageata_durata_vs"),
              valori: `δ = ${fmt(g.sls.sageata.delta_mm, 2)} mm ≤ ${fmt(g.sls.sageata.lim_250_mm)} mm (l₀/250); l₀/500 = ${fmt(g.sls.sageata.lim_500_mm)} mm`,
              ref: "EC2 §7.4.1(4)–(5)",
            },
          ]}
        >
          <div style={{ marginBottom: 8 }}>
            {t("struct.arg.sageata_eticheta")} <Verdict v={g.sls.sageata.verdict} marja={g.sls.sageata.acceptat_marja} />
            {g.sls.sageata.verdict.startsWith("NU") && (
              <span className="sg-muted">{t("struct.arg.otelul_nu_mai_ajuta")}</span>
            )}
          </div>
          <table className="sg-table">
            <thead>
              <tr>
                <th>{t("struct.arg.sectiunea")}</th>
                <th title={t("struct.arg.momentul_cvasipermanent")}>M_GS (kNm)</th>
                <th title={t("struct.arg.efortul_armatura_intinsa")}>σ_s (MPa)</th>
                <th title={t("struct.arg.distanta_maxima_intre")}>s_r,max (mm)</th>
                <th title={t("struct.arg.deschiderea_caracteristi")}>w_k (mm)</th>
                <th>{t("struct.arg.th_limita")}</th>
                <th>{t("struct.arg.th_verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {g.sls.fisuri.map((f, i) => (
                <tr key={i}>
                  <td className="sg-mono" title={numelePunctului(f.punct)}>{f.punct}</td>
                  <td className="sg-mono">{fmt(f.M_knm)}</td>
                  <td className="sg-mono">{f.sigma_s !== undefined ? fmt(f.sigma_s) : "—"}</td>
                  <td className="sg-mono">{f.s_rmax_mm !== undefined ? fmt(f.s_rmax_mm, 0) : "—"}</td>
                  <td className="sg-mono"><strong>{fmt(f.wk_mm, 3)}</strong></td>
                  <td className="sg-mono">{fmt(g.sls!.w_max_mm, 1)}</td>
                  <td><Verdict v={f.verdict} marja={f.acceptat_marja} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="sg-muted" style={{ marginTop: 6, fontSize: 12 }}>
            {t("struct.arg.wk_formula")}
          </div>
        </Sectiune>
      )}

      {/* --- 10. Rotiri capabile ---------------------------------------------------- */}
      <Sectiune
        titlu={t("struct.arg.10_rotirile_capabile")}
        explicatie={t("struct.arg.s10_explicatie", {
          alpha: g.rotiri_capabile.alpha_confinare, nota: g.rotiri_capabile.nota,
        })}
      >
        <div className="sg-mono" style={{ fontSize: 13 }}>
          {Object.entries(g.rotiri_capabile.theta).map(([k, v]) => (
            <span key={k} style={{ marginRight: 16 }}>θ_cap({k}) = {v} rad</span>
          ))}
        </div>
        <div className="sg-muted" style={{ marginTop: 4 }}>
          {t("struct.arg.lv_deschiderea_forfecare", {
            r1: g.rotiri_capabile.Lv_m.R1, r2: g.rotiri_capabile.Lv_m.R2,
            rho: g.rotiri_capabile.rho_sw,
          })}
        </div>
      </Sectiune>

      {/* --- 10b. Reguli constructive ------------------------------------------- */}
      <Sectiune
        titlu={t("struct.arg.10b_reguli_constructive")}
        explicatie={t("struct.arg.s10b_explicatie", {
          spatiu: fmt(g.b_cm - 2 * Number(p.c_sus_cm ?? 5)
            - 2 * Number(g.armare.etrieri.phi ?? 8) / 10),
          imin: String(p.interax_min_cm ?? 5),
          imax: String(p.s_max_bare_cm ?? 18),
          phimin: String(p.phi_min_long_mm ?? 14),
          plafon: g.plafon_phi_nod_mm
            ? t("struct.arg.plafon_mm", { v: g.plafon_phi_nod_mm })
            : t("struct.arg.plafon_dezactivat"),
          gamma: zecGamma(p.gamma_rd_ancorare),
          sursa: String(p.gamma_rd_ancorare_sursa ?? ""),
        })}
      >
        <table className="sg-table">
          <thead>
            <tr>
              <th>{t("struct.arg.th_verificarea")}</th>
              <th>{t("struct.arg.th_e_cerut")}</th>
              <th>{t("struct.arg.th_r_limita")}</th><th>R/E</th>
              <th>{t("struct.arg.th_verdict")}</th>
            </tr>
          </thead>
          <tbody>
            {g.verificari
              .filter((v) => v.grupa === "constructiv" || v.grupa === "EC2"
                || v.nume.startsWith("A_s,jos"))
              .map((v, i) => (
                <tr key={i}>
                  <td title={v.referinta}>
                    <TitluVerificare catalog={catalog} pas="armare_grinzi"
                                     nume={v.nume} grupa={v.grupa}
                                     aratPasul={false} />
                  </td>
                  <td className="sg-mono">{typeof v.E === "number" ? fmt(v.E) : v.E}</td>
                  <td className="sg-mono">{typeof v.R === "number" ? fmt(v.R) : v.R}</td>
                  <td className="sg-mono">{v.RE === null ? "—" : fmt(v.RE, 2)}</td>
                  <td><Verdict v={v.verdict} marja={v.acceptat_marja} /></td>
                </tr>
              ))}
          </tbody>
        </table>
        {g.plafon_depasit && (
          // Explicația barelor ALESE, lipită de ele — nu un avertisment separat.
          // Avertismentul de dinainte spunea «ancorarea va pica la
          // Pasul 15», adică repeta un verdict care există deja acolo; ce
          // lipsea era motivul pentru care barele grinzii sunt mai groase decât
          // ar fi ieșit din calcul.
          <div className="st-warn" style={{ marginTop: 6 }}>
            {t("struct.arg.plafon_depasit", {
              plafon: g.plafon_phi_nod_mm
                ? t("struct.arg.plafon_paren", { v: g.plafon_phi_nod_mm })
                : "",
            })}
          </div>
        )}
        <div className="sg-muted" style={{ marginTop: 6 }}>
          {t("struct.arg.armarea_efectiva", {
            sus: `${g.armare.sus.continue.n}Ø${g.armare.sus.continue.phi}`,
            suplr1: g.armare.sus.supl_r1?.n
              ? t("struct.arg.supl_reazem_1", {
                  n: g.armare.sus.supl_r1.n, phi: g.armare.sus.supl_r1.phi })
              : "",
            suplr2: g.armare.sus.supl_r2?.n
              ? t("struct.arg.supl_reazem_2", {
                  n: g.armare.sus.supl_r2.n, phi: g.armare.sus.supl_r2.phi })
              : "",
            jos: `${g.armare.jos.continue.n}Ø${g.armare.jos.continue.phi}`,
            suplcamp: g.armare.jos.supl_camp?.n
              ? t("struct.arg.supl_camp_scurt", {
                  n: g.armare.jos.supl_camp.n, phi: g.armare.jos.supl_camp.phi })
              : "",
            phi: g.armare.etrieri.phi,
            critic: g.armare.etrieri.pas_critic,
            curent: g.armare.etrieri.pas_curent,
          })}
        </div>
      </Sectiune>

      {/* --- 11. Interfața către stâlpi ------------------------------------------ */}
      <Sectiune
        titlu={t("struct.arg.11_interfata_catre")}
        explicatie={t("struct.arg.s11_explicatie")}
      >
        <div className="sg-mono" style={{ fontSize: 13 }}>
          M_Rb: {Object.entries(g.interfata_stalpi.M_Rb_knm)
            .map(([k, v]) => `${k} = ${v} kNm`)
            .join(" · ")}
          <br />
          V_Ed,cap = {g.interfata_stalpi.V_Ed_cap_kn} kN · l_cr ={" "}
          {g.interfata_stalpi.l_cr_m} m
        </div>
      </Sectiune>
    </>
  );
}

/** Câte laturi ale grinzii au placă activă, în cuvinte. Șirurile stau în
 *  catalog: în altă limbă nu sunt trei forme ale aceluiași cuvânt. */
function parteaPlacii(n: number): string {
  return t(([
    "struct.arg.nicio_parte", "struct.arg.o_parte", "struct.arg.ambele_parti",
  ] as const)[n] ?? "struct.arg.nicio_parte");
}

function maxPhiSus(g: GrindaArmata) {
  return Math.max(
    g.armare.sus.continue.phi,
    g.armare.sus.supl_r1.phi || 0,
    g.armare.sus.supl_r2.phi || 0
  );
}
function rand2Sus(g: GrindaArmata) {
  return g.armare.sus.supl_r1.rand === 2 || g.armare.sus.supl_r2.rand === 2;
}

// ---------------------------------------------------------------------------
// Secțiune de „notă de calcul" — card cu explicație + formule cu valori
// ---------------------------------------------------------------------------

function Sectiune({
  titlu,
  explicatie,
  formule,
  termeni,
  children,
}: {
  titlu: string;
  explicatie: string;
  formule?: { eticheta: string; valori: string; ref: string }[];
  termeni?: [string, string][];
  children?: React.ReactNode;
}) {
  useT();
  // explicațiile lungi se strâng: rămâne prima frază,
  // restul la un clic — altfel cifrele grinzii ajung sub două ecrane de text
  const [totText, setTotText] = useState(false);
  const lung = explicatie.length > 300;
  const primaFraza = explicatie.split(/(?<=\.)\s/)[0];
  return (
    <div className="st-findings">
      <h3>{titlu}</h3>
      <div className="sg-muted" style={{ marginBottom: 8 }}>
        {!lung || totText ? explicatie : primaFraza}
        {lung && (
          <button className="ex-toggle" style={{ marginLeft: 6 }}
                  onClick={() => setTotText(!totText)}>
            {t(totText ? "struct.arg.strange" : "struct.arg.arata_tot")}
          </button>
        )}
      </div>
      {termeni && (
        <details style={{ marginBottom: 8 }}>
          <summary className="sg-muted" style={{ cursor: "pointer" }}>
            {t("struct.arg.termenii_folositi_romane")}
          </summary>
          <ul className="sg-muted" style={{ marginTop: 4 }}>
            {termeni.map(([termen, d], i) => (
              <li key={i}><strong>{termen}</strong> — {d}</li>
            ))}
          </ul>
        </details>
      )}
      {formule && (
        <table className="sg-table" style={{ marginBottom: 8 }}>
          <tbody>
            {formule.map((f, i) => (
              <tr key={i}>
                <td>{f.eticheta}<div className="sg-muted" style={{ fontSize: 11 }}>{f.ref}</div></td>
                <td className="sg-mono" style={{ fontSize: 13 }}>{f.valori}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {children}
    </div>
  );
}

function TabelPuncte({ g }: { g: GrindaArmata }) {
  useT();
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="sg-table">
        <thead>
          <tr>
            <th>{t("struct.arg.th_punct")}</th>
            <th>{t("struct.arg.th_grupare")}</th>
            <th title={t("struct.arg.momentul_calcul_negativ")}>M_Ed (kNm)</th>
            <th title={t("struct.arg.pozitia_statiei_axul")}>x (m)</th>
            <th title={t("struct.arg.forta_axiala_concomitent2")}>N (kN)</th>
            <th title={t("struct.arg.combinatia_da_extremul")}>{t("struct.arg.combinatia")}</th>
          </tr>
        </thead>
        <tbody>
          {(["fundamentala", "seismica"] as const).flatMap((gr) =>
            PUNCTE.map((pct) => {
              const x = g.puncte[gr][pct];
              return (
                <tr key={`${gr}-${pct}`}>
                  <td className="sg-mono" title={numelePunctului(pct)}>{pct}</td>
                  <td className="sg-muted">{gr}</td>
                  <td className="sg-mono"><strong>{fmt(x.M)}</strong>{x.M_necorectat !== undefined
                    ? t("struct.arg.corectia_4_5", { m: fmt(x.M_necorectat) })
                    : ""}</td>
                  <td className="sg-mono">{fmt(x.x, 2)}</td>
                  <td className="sg-mono">{fmt(x.N)}</td>
                  <td className="sg-mono">{x.combo}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

function FormulaExemplu({ g, p }: { g: GrindaArmata; p: ArmareResult["parametri"] }) {
  const geo = g.geometrie_sectiune;
  const arm = g.armare;
  const aSus =
    arm.sus.continue.n * Math.PI * (arm.sus.continue.phi / 10) ** 2 / 4 +
    (arm.sus.supl_r1.n
      ? arm.sus.supl_r1.n * Math.PI * ((arm.sus.supl_r1.phi || 12) / 10) ** 2 / 4
      : 0);
  return (
    <div className="sg-muted" style={{ marginTop: 8, fontSize: 13 }}>
      {t("struct.arg.formula_exemplu", {
        as: aSus.toFixed(2), fyd: p.f_yd_mpa, z: geo.z.R1,
        placa: g.n_placi.R1 > 0
          ? t("struct.arg.formula_exemplu_placa", { coef: p.coef_placa_fund ?? 0.8 })
          : t("struct.arg.formula_exemplu_fara_placa"),
      })}
      <strong>{fmt(g.m_rd.fundamentala.R1_sus)} kNm</strong>{" "}
      {t("struct.arg.formula_exemplu_sursa", {
        bare: `${arm.sus.continue.n}Ø${arm.sus.continue.phi}`
          + (arm.sus.supl_r1.n ? ` + ${arm.sus.supl_r1.n}Ø${arm.sus.supl_r1.phi}` : ""),
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Secțiunea transversală (SVG)
// ---------------------------------------------------------------------------

export function SectiuneTransversala({ g, unde }: { g: GrindaArmata; unde: "reazem" | "camp" }) {
  const b = g.b_cm, h = g.h_cm;
  const sc = 3.2;
  // caseta face loc cotelor: două raiuri la stânga (h, acoperirea), una
  // dedesubt (b) și una la dreapta (grosimea plăcii) — Stefan
  const ox = 132, oy = 30;
  const W = ox + b * sc + 210, H = oy + h * sc + 46;
  const arm = g.armare;
  const c = 5;
  const hPl = g.h_pl_cm ?? 0;
  const bare: { x: number; y: number; phi: number; col: string }[] = [];
  const puneRand = (set: { n: number; phi: number }, y: number, col: string) => {
    if (!set.n) return;
    const u = b - 2 * c;
    for (let i = 0; i < set.n; i++) {
      const x = set.n === 1 ? b / 2 : c + (u * i) / (set.n - 1);
      bare.push({ x, y, phi: set.phi, col });
    }
  };
  puneRand(arm.sus.continue, c + arm.sus.continue.phi / 20, "#b5323d");
  const suplSus = unde === "reazem" ? arm.sus.supl_r1 : { n: 0, phi: 0 };
  if (suplSus.n)
    puneRand(suplSus, suplSus.rand === 2 ? c + 4 : c + 1.2, "#a97614");
  puneRand(arm.jos.continue, h - c - arm.jos.continue.phi / 20, "#2b5f8a");
  if (unde === "camp" && arm.jos.supl_camp.n)
    puneRand(arm.jos.supl_camp, arm.jos.supl_camp.rand === 2 ? h - c - 4 : h - c - 1.2, "#2e7d72");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, background: "#fff", border: "1px solid #dedede", borderRadius: 6 }}>
      <text x={ox} y={16} fontSize={12} fill="#555">
        {t("struct.arg.sectiunea_la", {
          unde: t(unde === "reazem"
            ? "struct.arg.la_reazem_r1" : "struct.arg.la_mijlocul_campului"),
        })}
      </text>
      <rect x={ox} y={oy} width={b * sc} height={h * sc} fill="#ececec" stroke="#737373" />
      {/* PLACA: partea de SUS a lui h, nu o bandă adăugată peste ea (fața de
          sus a grinzii și a plăcii coincid — v. Grinda3D) */}
      {hPl > 0 && (
        <>
          <rect x={ox - 26} y={oy} width={b * sc + 52} height={hPl * sc}
                fill="#dbdfe4" stroke="#8f9298" strokeWidth={0.9} />
          <HasuraPlaca x={ox - 26} y={oy} w={b * sc + 52} h={hPl * sc} />
        </>
      )}
      <rect x={ox + (c - 0.8) * sc} y={oy + (c - 0.8) * sc} width={(b - 2 * c + 1.6) * sc} height={(h - 2 * c + 1.6) * sc} fill="none" stroke="#232323" strokeWidth={1.4} rx={4} />
      {bare.map((p, i) => (
        <circle key={i} cx={ox + p.x * sc} cy={oy + p.y * sc} r={(p.phi / 20) * sc} fill={p.col} />
      ))}
      {/* COTELE */}
      <CotaH x1={ox} x2={ox + b * sc} y={oy + h * sc + 24}
             martorY={oy + h * sc} text={`b = ${cmFmt(b)} cm`} />
      <CotaV x={ox - 30} y1={oy} y2={oy + h * sc} martorX={ox}
             text={`h = ${cmFmt(h)} cm`} />
      <CotaV x={ox - 66} y1={oy + h * sc - (c - 0.8) * sc} y2={oy + h * sc}
             martorX={ox} text={`c = ${cmFmt(c)} cm`} size={9} />
      {hPl > 0 && (
        <CotaV x={ox + b * sc + 44} y1={oy} y2={oy + hPl * sc}
               martorX={ox + b * sc + 26} laDreapta
               text={t("struct.arg.cota_placa", { v: cmFmt(hPl) })} size={9.5} />
      )}
      <text x={ox + b * sc + 76} y={oy + 12} fontSize={11} fill="#777">{b}×{h} cm</text>
      <text x={ox + b * sc + 76} y={oy + 28} fontSize={11} fill="#b5323d">{t("struct.arg.svg_sus", { arm: `${arm.sus.continue.n}Ø${arm.sus.continue.phi}${unde === "reazem" && arm.sus.supl_r1.n ? ` + ${arm.sus.supl_r1.n}Ø${arm.sus.supl_r1.phi}` : ""}` })}</text>
      <text x={ox + b * sc + 76} y={oy + 44} fontSize={11} fill="#2b5f8a">{t("struct.arg.svg_jos", { arm: `${arm.jos.continue.n}Ø${arm.jos.continue.phi}${unde === "camp" && arm.jos.supl_camp.n ? ` + ${arm.jos.supl_camp.n}Ø${arm.jos.supl_camp.phi}` : ""}` })}</text>
      <text x={ox + b * sc + 76} y={oy + 60} fontSize={11} fill="#232323">{t("struct.arg.svg_etr", { phi: arm.etrieri.phi })}</text>
      <text x={ox + b * sc + 76} y={oy + 76} fontSize={10} fill="#737373">{t("struct.arg.svg_h_include_placa")}</text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// 3D — grinda cu armătura, placa activă și contextul
// ---------------------------------------------------------------------------

export function Grinda3D({
  g,
  ax,
  croire,
}: {
  g: GrindaArmata;
  /** deschiderile AXULUI coliniar, în ordinea de pe ax (`armare_grinzi.
   *  axe_coliniare`). Când vine, scena le pune cap la cap și o estompează pe
   *  fiecare în afară de `g`; fără ea rămâne exact vederea de până acum.
   *  Deschiderea parcursă invers pe ax se rotește cu π în jurul lui Z — o
   *  rotație PROPRIE, nu o oglindire cu scale negativ: normalele ar ieși pe
   *  dos, iar iluminarea ar arăta betonul întors pe faţă. */
  ax?: GrindaArmata[];
  /** croiala barelor pe ax (Pasul 13): din ea se desenează suprapunerile.
   *  Fără ea, scena rămâne exact vederea de până acum. */
  croire?: CroireAx | null;
}) {
  useT();
  const mount = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = mount.current;
    if (!el) return;
    const W = el.clientWidth || 900;
    const H = 380;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3f6f9);
    const camera = new THREE.PerspectiveCamera(40, W / H, 0.01, 100);
    // preserveDrawingBuffer: canvas-ul rămâne citibil după randare (captură
    // de ecran / export), ca la modelul Pasului 7
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    el.appendChild(renderer.domElement);

    // tot ce se alocă pe GPU și nu e prins de renderer.dispose() (texturi,
    // materiale, geometrii) se strânge aici; altfel schimbarea grinzii din
    // dropdown scurge un set complet de sprite-uri
    const gunoi: { dispose(): void }[] = [];

    // eticheta text = sprite din canvas (aceeași rețetă ca la ceapa P–M–M din
    // StepArmareStalpi)
    const sprite = (parinte: THREE.Object3D, txt: string, x: number,
                    y: number, z: number, col: string, w: number) => {
      const cv = document.createElement("canvas");
      cv.width = 384; cv.height = 64;
      const g2 = cv.getContext("2d");
      if (!g2) return;
      g2.fillStyle = col;
      g2.font = "bold 34px system-ui, sans-serif";
      g2.textAlign = "center"; g2.textBaseline = "middle";
      g2.fillText(txt, 192, 34);
      const tex = new THREE.CanvasTexture(cv);
      const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
      const sp = new THREE.Sprite(mat);
      sp.position.set(x, y, z);
      sp.scale.set(w, (w * cv.height) / cv.width, 1);
      parinte.add(sp);
      gunoi.push(tex, mat);
    };

    // Construirea UNEI deschideri, într-un părinte propriu: pe ax se cheamă
    // o dată pentru fiecare, iar grupul se translatează/rotește la locul lui.
    const construieste = (
      gr: GrindaArmata, parinte: THREE.Object3D, curenta: boolean,
      // pe ax, capătul care are o deschidere REALĂ alături nu mai primește
      // ciotul de „grindă vecină": ar sta înfipt în betonul vecinei
      ciotVecin: [boolean, boolean] = [true, true]
    ) => {
      const L = gr.L_ax_m;
      const b = gr.b_cm / 100, h = gr.h_cm / 100, hp = gr.h_pl_cm / 100;
      const c = 0.05;
      const gri = (op: number, col = 0x9aa0a8) =>
        new THREE.MeshStandardMaterial({ color: col, transparent: true, opacity: op, depthWrite: false });

      // grinda (beton transparent) — axa X, fața de sus la z = 0
      const grinda = new THREE.Mesh(new THREE.BoxGeometry(L, b, h), gri(0.22));
      grinda.position.set(L / 2, 0, -h / 2);
      parinte.add(grinda);
      parinte.add(new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.BoxGeometry(L, b, h)),
        new THREE.LineBasicMaterial({ color: 0x777777 })
      ).translateX(L / 2).translateZ(-h / 2));

      // placa (context) + fâșia activă b_eff
      const ext = 0.9;
      for (const [cap, [st, dr]] of Object.entries(gr.laturi_placa)) {
        void cap;
        const laturi: [boolean, number][] = [[st, 1], [dr, -1]];
        for (const [are, s] of laturi) {
          if (!are) continue;
          const placa = new THREE.Mesh(new THREE.BoxGeometry(L, ext, hp), gri(0.14, 0xb9bfc7));
          placa.position.set(L / 2, s * (b / 2 + ext / 2), -hp / 2);
          parinte.add(placa);
          const beff = (gr.b_eff_ext_cm.R1 || 0) / 100 / Math.max(gr.n_placi.R1, 1);
          if (beff > 0) {
            const act = new THREE.Mesh(new THREE.BoxGeometry(L, beff, hp), new THREE.MeshStandardMaterial({ color: 0x1d7a4b, transparent: true, opacity: 0.30, depthWrite: false }));
            act.position.set(L / 2, s * (b / 2 + beff / 2), -hp / 2);
            parinte.add(act);
          }
        }
        break; // placa se desenează o dată pe toată lungimea (starea de la R1)
      }

      // stâlpii de capăt + grinzile vecine (context schițat)
      const capete: [number, number, boolean][] = [
        [0, gr.off_i_m, ciotVecin[0]],
        [L, gr.off_j_m, ciotVecin[1]],
      ];
      for (const [x0, off, cuCiot] of capete) {
        const stalp = new THREE.Mesh(new THREE.BoxGeometry(2 * off, Math.max(b, 0.3), h + 1.5), gri(0.30, 0x7d858f));
        stalp.position.set(x0, 0, -(h + 1.5) / 2 + 0.45);
        // `context`: schițele astea NU intră în cutia de încadrat (v. mai jos)
        stalp.userData.context = true;
        parinte.add(stalp);
        if (!cuCiot) continue;
        const vecin = new THREE.Mesh(new THREE.BoxGeometry(0.55, b, h), gri(0.12));
        vecin.position.set(x0 + (x0 === 0 ? -1 : 1) * (off + 0.3), 0, -h / 2);
        vecin.userData.context = true;
        parinte.add(vecin);
      }

      // barele longitudinale (cilindri)
      const bara = (x1: number, x2: number, y: number, z: number, phi: number, col: number) => {
        const geo = new THREE.CylinderGeometry(phi / 2000, phi / 2000, Math.max(x2 - x1, 0.01), 10);
        const m = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: col }));
        m.rotation.z = Math.PI / 2;
        m.position.set((x1 + x2) / 2, y, z);
        parinte.add(m);
      };
      const spread = (n: number) => {
        const u = b - 2 * c;
        return Array.from({ length: n }, (_, i) => (n === 1 ? 0 : -u / 2 + (u * i) / (n - 1)));
      };
      const a = gr.armare;
      const zSus = -(c + a.sus.continue.phi / 2000);
      const zJos = -h + c + a.jos.continue.phi / 2000;
      for (const y of spread(a.sus.continue.n)) bara(-gr.off_i_m, L + gr.off_j_m, y, zSus, a.sus.continue.phi, 0xc0392b);
      for (const y of spread(a.jos.continue.n)) bara(-gr.off_i_m, L + gr.off_j_m, y, zJos, a.jos.continue.phi, 0x2471a3);
      const lcr = gr.interfata_stalpi.l_cr_m;
      if (a.sus.supl_r1.n)
        for (const y of spread(a.sus.supl_r1.n)) bara(0, Math.min(gr.off_i_m + lcr + 0.8, L * 0.45), y, zSus - (a.sus.supl_r1.rand === 2 ? 0.04 : 0.012), a.sus.supl_r1.phi, 0xa97614);
      if (a.sus.supl_r2.n)
        for (const y of spread(a.sus.supl_r2.n)) bara(Math.max(L - gr.off_j_m - lcr - 0.8, L * 0.55), L, y, zSus - (a.sus.supl_r2.rand === 2 ? 0.04 : 0.012), a.sus.supl_r2.phi, 0xa97614);
      if (a.jos.supl_camp.n)
        for (const y of spread(a.jos.supl_camp.n)) bara(L * 0.22, L * 0.78, y, zJos + (a.jos.supl_camp.rand === 2 ? 0.04 : 0.012), a.jos.supl_camp.phi, 0x17a589);

      // etrierii — dreptunghiuri de linii, îndesiți în zonele critice
      const etr: number[] = [];
      const x1 = gr.off_i_m, x2 = L - gr.off_j_m;
      const pasC = a.etrieri.pas_critic / 100, pasU = a.etrieri.pas_curent / 100;
      let x = x1 + 0.03;
      while (x < x2) {
        etr.push(x);
        x += x < x1 + lcr || x > x2 - lcr ? pasC : pasU;
      }
      const pts: number[] = [];
      const y1 = -(b / 2 - c), y2 = b / 2 - c, zt = -c, zb = -h + c;
      for (const xe of etr) {
        pts.push(xe, y1, zt, xe, y2, zt, xe, y2, zt, xe, y2, zb,
                 xe, y2, zb, xe, y1, zb, xe, y1, zb, xe, y1, zt);
      }
      const geoE = new THREE.BufferGeometry();
      geoE.setAttribute("position", new THREE.Float32BufferAttribute(pts, 3));
      parinte.add(new THREE.LineSegments(geoE, new THREE.LineBasicMaterial({ color: 0x2c3e70 })));

      // -----------------------------------------------------------------------
      // ZONELE RIGIDE (bucata de grindă din interiorul stâlpului) + cota l₀
      // Se desenează la sfârșit, ca să stea peste betonul transparent, dar
      // semitransparent și fără depthWrite, ca barele să rămână vizibile
      // dedesubt. Fără marcajul asta nu se vede DE CE l₀ ≠ L_ax, iar cifra din
      // titlu pare o eroare de rotunjire.
      // -----------------------------------------------------------------------

      // eticheta text = sprite din canvas (aceeași rețetă ca la ceapa P–M–M din
      // StepArmareStalpi); scalată relativ la L, altfel pe grinzi de 3 m textul
      // acoperă toată grinda și pe cele de 16 m devine ilizibil
      const swT = Math.min(1.7, Math.max(0.55, L * 0.13));
      const eticheta = (txt: string, x: number, y: number, z: number,
                        col = "#b8730c", w = swT) => {
        // pe un ax de trei deschideri, trei cote l₀ și șase etichete «rigid» se
        // calcă una pe alta: le poartă numai deschiderea din pagină
        if (!curenta) return;
        sprite(parinte, txt, x, y, z, col, w);
      };

      const matRigid = new THREE.MeshStandardMaterial({
        color: 0x8e3b73, transparent: true, opacity: 0.45, depthWrite: false,
      });
      gunoi.push(matRigid);
      const matRigidMuchii = new THREE.LineBasicMaterial({ color: 0x5e2449 });
      gunoi.push(matRigidMuchii);

      // [x_start, lungime] pentru cele două capete; un off lipsă sau 0 (stâlp
      // fără gabarit pe direcția grinzii, sau capăt liber) nu desenează nimic
      const zoneRigide: [number, number][] = [
        [0, gr.off_i_m || 0],
        [L - (gr.off_j_m || 0), gr.off_j_m || 0],
      ];
      for (const [xs, len] of zoneRigide) {
        if (!(len > 0.001)) continue;
        // 1,02× secțiunea, ca muchiile să nu se suprapună exact cu ale grinzii
        const geoR = new THREE.BoxGeometry(len, b * 1.02, h * 1.02);
        gunoi.push(geoR);
        const cutie = new THREE.Mesh(geoR, matRigid);
        cutie.position.set(xs + len / 2, 0, -h / 2);
        parinte.add(cutie);
        const geoRM = new THREE.EdgesGeometry(geoR);
        gunoi.push(geoRM);
        const muchii = new THREE.LineSegments(geoRM, matRigidMuchii);
        muchii.position.copy(cutie.position);
        parinte.add(muchii);
        eticheta(`rigid ${fmtCm(len)} cm`, xs + len / 2, 0, 0.52 + swT * 0.09);
      }

      // cota l₀: linie subțire sub grindă, între FEȚELE stâlpilor, cu ticks.
      // Eticheta stă DEASUPRA liniei, cu 14 cm liberi până la fața de jos a
      // grinzii; de-aia și linia coboară cu jumătatea de înălțime a etichetei
      // (pe grinzi lungi sprite-ul e mai mare și altfel ar atinge betonul)
      const xa = gr.off_i_m || 0, xb = L - (gr.off_j_m || 0);
      if (xb > xa) {
        const zEt = -h - 0.14 - swT * 0.09;
        const zc = zEt - swT * 0.09 - 0.04, t = 0.07;
        const geoC = new THREE.BufferGeometry();
        geoC.setAttribute("position", new THREE.Float32BufferAttribute([
          xa, 0, zc, xb, 0, zc,          // linia de cotă
          xa, 0, zc - t, xa, 0, zc + t,  // tick la fața stâlpului i
          xb, 0, zc - t, xb, 0, zc + t,  // tick la fața stâlpului j
        ], 3));
        gunoi.push(geoC);
        const matC = new THREE.LineBasicMaterial({ color: 0x2c3e70 });
        gunoi.push(matC);
        parinte.add(new THREE.LineSegments(geoC, matC));
        eticheta(`l₀ = ${fmtM(gr.l0_m)} m`, (xa + xb) / 2, 0, zEt, "#232323");
      }


      // deschiderile vecine se sting: ele sunt CONTEXTUL, nu subiectul
      if (!curenta) {
        parinte.traverse((o) => {
          const m = (o as THREE.Mesh).material as THREE.Material | undefined;
          if (!m || Array.isArray(m)) return;
          m.transparent = true;
          m.opacity = (m.opacity ?? 1) * 0.45;
        });
      }
    };

    // lista de desenat: axul întreg sau numai grinda
    const lista = ax && ax.length > 1 ? ax : [g];
    const Ltot = lista.length > 1
      ? (Number(g.ax?.L_ax_m)
         || lista.reduce((a, z) => a + (Number(z.L_ax_m) || 0), 0))
      : g.L_ax_m;
    const hMax = Math.max(...lista.map((z) => (Number(z.h_cm) || 50) / 100));
    lista.forEach((gr, k) => {
      const grup = new THREE.Group();
      let ciot: [boolean, boolean] = [true, true];
      if (lista.length > 1) {
        const s0 = Number(gr.ax?.s0_m) || 0;
        const inv = !!gr.ax?.inversata;
        if (inv) {
          grup.rotation.z = Math.PI;
          grup.position.set(s0 + (Number(gr.L_ax_m) || 0), 0, 0);
        } else {
          grup.position.set(s0, 0, 0);
        }
        // capătul dinspre începutul axului / dinspre sfârșitul lui; pe o
        // deschidere parcursă invers, nodul i al grinzii e cel din DREAPTA
        const areInainte = k > 0, areInapoi = k < lista.length - 1;
        ciot = inv
          ? [!areInapoi, !areInainte]
          : [!areInainte, !areInapoi];
      }
      construieste(gr, grup, gr.element === g.element, ciot);
      scene.add(grup);
    });

    // ------------------------------------------------------------------
    // PETRECERILE. Bara se livrează la 12 m: un traseu mai lung se taie, iar
    // bucățile se petrec pe l₀. O petrecere NU primește culoare proprie —
    // e aceeași bară, de două ori: se desenează dedublat, în culoarea
    // familiei ei, cu cota l₀ deasupra. Marcajul stă în abscisa AXULUI,
    // aceeași în care sunt așezate grupurile deschiderilor.
    // ------------------------------------------------------------------
    if (croire) {
      // Croiala e a AXULUI întreg. Când în pagină e o singură deschidere,
      // scena e locală: abscisele se mută cu s0, se oglindesc dacă deschiderea
      // e parcursă invers (aceeași regulă ca în elevație — v.
      // `petreceriPeDeschidere`) și rămân NUMAI petrecerile căzute pe
      // deschiderea asta. Altfel, pe un ax de patru deschideri, înnădirile
      // celorlalte stăteau ca niște cilindri și etichete în gol, la 7 și la
      // 17 m de o grindă de 5 m, iar încadrarea — care măsoară toată scena —
      // strângea grinda la un sfert de cadru ca să le cuprindă și pe ele.
      const unaSingura = lista.length === 1;
      // abscisa din SCENĂ a mijlocului petrecerii; `null` = nu e pe
      // deschiderea desenată
      const abscisa = (mij: number): number | null =>
        unaSingura ? peDeschidere(g, mij) : mij;
      for (const t of croire.trasee) {
        const sus = t.rol.includes("sus");
        const col = sus ? 0xb5323d : 0x2b5f8a;
        const matL = new THREE.MeshStandardMaterial({ color: col });
        const matF = new THREE.MeshStandardMaterial({ color: 0xa97614 });
        gunoi.push(matL, matF);
        for (const i of t.innadiri) {
          const xc = abscisa((i.s_m + i.s2_m) / 2);
          if (xc == null) continue;
          const gazda = unaSingura ? g : (lista.find((z) => {
            const s0 = Number(z.ax?.s0_m) || 0;
            return i.s_m >= s0 && i.s_m <= s0 + (Number(z.L_ax_m) || 0);
          }) || g);
          const h = (Number(gazda.h_cm) || 50) / 100;
          const c = 0.05;
          const phi = t.phi_mm / 1000;
          // a doua bară, decalată cu un diametru spre interiorul secțiunii
          const z = (sus ? -(c + phi / 2) : -h + c + phi / 2)
                    + (sus ? -phi : phi);
          const len = Math.max(i.s2_m - i.s_m, 0.02);
          const m = new THREE.Mesh(
            new THREE.CylinderGeometry(phi / 2, phi / 2, len, 10),
            i.fortata ? matF : matL
          );
          m.rotation.z = Math.PI / 2;
          m.position.set(xc, 0, z);
          scene.add(m);
          sprite(scene, `l₀ ${i.l0_m.toFixed(2)} m`,
                 xc, 0, z + (sus ? 0.26 : -0.26),
                 i.fortata ? "#a97614" : (sus ? "#b5323d" : "#2b5f8a"),
                 Math.min(1.7, Math.max(0.55, Ltot * 0.09)));
        }
      }
      // zonele CRITICE: acolo P100-1 §5.7.3(1) nu admite petreceri, iar fără
      // ele poziția petrecerii pare aleasă la întâmplare
      const matZc = new THREE.MeshStandardMaterial({
        color: 0xb5323d, transparent: true, opacity: 0.13, depthWrite: false,
      });
      gunoi.push(matZc);
      for (const gr of lista) {
        const s0 = (lista.length > 1 ? Number(gr.ax?.s0_m) || 0 : 0);
        const L = Number(gr.L_ax_m) || 0;
        const lcr = Number(gr.interfata_stalpi?.l_cr_m) || 0;
        const b = (Number(gr.b_cm) || 30) / 100;
        const h = (Number(gr.h_cm) || 50) / 100;
        if (!lcr) continue;
        for (const x0 of [s0 + (gr.off_i_m || 0), s0 + L - (gr.off_j_m || 0) - lcr]) {
          const zc = new THREE.Mesh(new THREE.BoxGeometry(lcr, b + 0.002, h), matZc);
          zc.position.set(x0 + lcr / 2, 0, -h / 2);
          scene.add(zc);
        }
      }
    }

    scene.add(new THREE.AmbientLight(0xffffff, 0.9));
    const dl = new THREE.DirectionalLight(0xffffff, 0.7);
    dl.position.set(2, -3, 4);
    scene.add(dl);

    // `camera.up` ÎNAINTE de OrbitControls: constructorul lui îngheață axa de
    // orbitare din up-ul de atunci („so camera.up is the orbit axis", cu
    // `_quat` calculat o singură dată). Setat după, controlul rotea în jurul
    // lui Y global, deși scena e Z-up — grinda nu urma mouse-ul, spre
    // deosebire de celelalte vizualizări.
    camera.up.set(0, 0, 1);
    camera.position.set(Ltot / 2 - Ltot * 0.35,
                        -Math.max(2.2, Ltot * 0.8), 0.9);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(Ltot / 2, 0, -hMax / 2);
    // Distanța reală o pune încadrarea, după ce scena e gata: o grindă lungă
    // ieșea din cadru pe lateral cu poziționarea „după lungime" de mai sus.
    // Se încadrează PIESA, nu scena: stâlpii de capăt sunt context schițat, iar
    // cutia lor (h + 1,5 m, cât să se vadă că stâlpul continuă peste și sub
    // grindă) e de câteva ori mai înaltă decât grinda — măsurată împreună cu
    // ei, grinda rămânea o dungă în mijlocul cadrului.
    const piesa = cutiaPiesei(scene);
    incadreaza(camera, controls, piesa.isEmpty() ? scene : piesa);

    // randare LA CERERE (nu buclă rAF): pane-urile ascunse opresc rAF, iar
    // scena e statică — se redesenează doar la interacțiune
    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);
    render();
    return () => {
      controls.removeEventListener("change", render);
      controls.dispose();
      gunoi.forEach((x) => x.dispose());
      renderer.dispose();
      el.removeChild(renderer.domElement);
    };
  }, [g, ax, croire]);

  return <div ref={mount} style={{ width: "100%", height: 380 }} />;
}
